import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Button, FormCheck, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import InputMask from "react-input-mask";
import Select from "react-select";

import ModalLoading from "../../components/ModalLoading/ModalLoading";
import DAddressForm from "../../components/DAddressForm/DAddressForm";
import DFormText from "../../components/DFormText/DFormText";

import { cnpj } from "cpf-cnpj-validator";
import api from '../../../services/api';
import dayjs from "dayjs";
import { createLog } from "../../../services/logService";

const TYPES_LIST = [
    { value: "MMP", label: "Material Médico de Procedimentos" },
    { value: "MDE", label: "Material de Escritório" },
    { value: "SDI", label: "Suplementos de Informática" },
    { value: "STI", label: "Suporte Técnico de Informática" },
    { value: "STT", label: "Suporte Técnico de Telefonia" },
    { value: "SI", label: "Sistemas de Informação" },
    { value: "EM", label: "Equipamentos Médicos" },
    { value: "SG", label: "Serviços Gerais" },
    { value: "ML", label: "Materiais de Limpeza" },
];

const SupplierForm = forwardRef((props, ref) => {
    const { onClose } = props;
    const [modalBox, setModalBox] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [loadingSupplierForm, setLoadingSupplierForm] = useState(false);

    const [supplierId, setSupplierId] = useState(undefined);

    const [name, setName] = useState("");
    const [shortName, setShortName] = useState("");
    const [documentId, setDocumentId] = useState("");
    const [type, setType] = useState(undefined);
    const [paymentType, setPaymentType] = useState("");
    const [createDate, setCreateDate] = useState(dayjs().format("DD/MM/YYYY"));

    const [addressInfo, setAddressInfo] = useState({});

    const [contactId, setContactId] = useState(undefined);
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactHomePhone, setContactHomePhone] = useState("");
    const [contactComercialPhone, setContactComercialPhone] = useState("");
    const [contactCellPhone, setContactCellPhone] = useState("");
    const [observation, setObservation] = useState("");
    const [active, setActive] = useState(true);

    const [invalidName, setInvalidName] = useState(false);
    const [invalidDocumentId, setInvalidDocumentId] = useState(false);
    const [invalidDocumentIdText, setInvalidDocumentIdText] = useState("Informe um CNPJ válido");
    const [invalidType, setInvalidType] = useState(false);
    const [invalidPaymentType, setInvalidPaymentType] = useState(false);

    useImperativeHandle(ref, () => ({
        openModal(mode, supplierInfo = {}) {
            setModalBox(true);
            setIsUpdate(false);

            resetFields();

            if (mode === "update") {
                setIsUpdate(true);

                setSupplierId(supplierInfo._id);
                setDocumentId(supplierInfo.document_id);
                setName(supplierInfo.name);
                setShortName(supplierInfo.short_name ?? "");
                setActive(supplierInfo.status);

                if (supplierInfo.type) {
                    const supplierType = TYPES_LIST.find(x => x.value === supplierInfo.type);
                    if (supplierType) {
                        setType(supplierType);
                    }
                }

                setPaymentType(supplierInfo.payment_type);
                setCreateDate(dayjs(supplierInfo.createdAt).format("DD/MM/YYYY"));
            }

            if (supplierInfo.addresses && supplierInfo.addresses.length > 0) {
                setAddressInfo({
                    zipCode: supplierInfo.addresses[0].zip_code,
                    address: supplierInfo.addresses[0].address,
                    addressNumber: supplierInfo.addresses[0].address_number,
                    complement: supplierInfo.addresses[0].complement ?? "",
                    district: supplierInfo.addresses[0].district ?? "",
                    city: supplierInfo.addresses[0].city ?? "",
                    addressState: supplierInfo.addresses[0].state
                });
            }

            if (supplierInfo.contacts && supplierInfo.contacts.length > 0) {
                setContactId(supplierInfo.contacts[0].id);
                setContactName(supplierInfo.contacts[0].name);
                setContactEmail(supplierInfo.contacts[0].email);

                for (let indexPhone = 0; indexPhone < supplierInfo.contacts[0].phones.length; indexPhone++) {
                    const phoneReturned = supplierInfo.contacts[0].phones[indexPhone].phone;

                    if (supplierInfo.contacts[0].phones[indexPhone].type === "home") {
                        setContactHomePhone(phoneReturned);
                    }
                    if (supplierInfo.contacts[0].phones[indexPhone].type === "comercial") {
                        setContactComercialPhone(phoneReturned);
                    }

                    if (supplierInfo.contacts[0].phones[indexPhone].type === "cell") {
                        setContactCellPhone(phoneReturned);
                    }
                }
            }
        },
    }));

    async function handleSaveSupplier(e) {
        e.preventDefault();

        setLoadingSupplierForm(true);

        setInvalidName(false);
        setInvalidType(false);
        setInvalidPaymentType(false);

        let hasError = false;

        const documentUnformatted = documentId.replace(/[.]/g, "").replace("/", "").replace("-", "");
        if (!isUpdate && (invalidDocumentId || documentUnformatted.trim() === "")) {
            setInvalidDocumentId(true);
            hasError = true;
        }

        if (name === "") {
            setInvalidName(true);
            hasError = true;
        }

        if (!type) {
            setInvalidType(true);
            hasError = true;
        }

        if (paymentType === "") {
            setInvalidPaymentType(true);
            hasError = true;
        }

        if (!hasError) {
            let supplier = {
                status: active,
                document_id: documentUnformatted.trim(),
                name,
                short_name: shortName,
                type: type.value,
                type_name: type.label,
                payment_type: paymentType,
                observation,
                addresses: [],
                contacts: []
            };

            if (addressInfo) {
                if (addressInfo.zipCode?.trim().length === 8) {
                    supplier.addresses.push({
                        zip_code: addressInfo.zipCode.trim(),
                        address: addressInfo.address,
                        address_number: addressInfo.addressNumber,
                        complement: addressInfo.complement ?? "",
                        district: addressInfo.district,
                        city: addressInfo.city,
                        state: addressInfo.addressState
                    });
                }
            }

            const contactHomePhoneUnformatted = contactHomePhone.replace(/[()-]/g, "").replace(" ", "");
            const contactComercialPhoneUnformatted = contactComercialPhone.replace(/[()-]/g, "").replace(" ", "");
            const contactCellPhoneUnformatted = contactCellPhone.replace(/[()-]/g, "").replace(" ", "");

            if (contactName !== "" || contactEmail !== "" || contactHomePhoneUnformatted.trim() !== "" || contactComercialPhoneUnformatted.trim() !== "" || contactCellPhoneUnformatted.trim() !== "") {
                let newContact = {
                    type: "personal",
                    name: contactName,
                    email: contactEmail,
                    phones: []
                };

                if (contactHomePhoneUnformatted) {
                    newContact.phones.push({
                        type: "home",
                        phone: contactHomePhoneUnformatted
                    });
                }

                if (contactComercialPhoneUnformatted) {
                    newContact.phones.push({
                        type: "comercial",
                        phone: contactComercialPhoneUnformatted
                    });
                }

                if (contactCellPhoneUnformatted) {
                    newContact.phones.push({
                        type: "cell",
                        phone: contactCellPhoneUnformatted
                    });
                }

                supplier.contacts.push(newContact);
            }

            if (!isUpdate) {
                try {
                    await api.post("/suppliers", supplier)
                        .then(response => {
                            if (response.status === 201) {
                                createLog({
                                    routine: "supplier",
                                    action: "create",
                                    type: "success",
                                    message: `Fornecedor ${supplier.name} incluído`,
                                    jsonOrigin: JSON.stringify(supplier),
                                    jsonReturn: JSON.stringify(response)
                                });

                                setLoadingSupplierForm(false);
                                notifySuccess();
                                resetFields();
                                onClose();
                                setModalBox(false);
                            }
                        })
                } catch (err) {
                    createLog({
                        routine: "supplier",
                        action: "create",
                        type: "error",
                        message: `Fornecedor ${supplier.name} incluído`,
                        jsonOrigin: JSON.stringify(supplier),
                        jsonReturn: JSON.stringify(err)
                    });

                    notifyError();
                }
            } else {
                try {
                    await api.put(`/suppliers/${supplierId}`, supplier)
                        .then(response => {
                            if (response.status === 200) {
                                createLog({
                                    routine: "supplier",
                                    action: "update",
                                    type: "success",
                                    message: `Fornecedor ${supplier.name} alterado`,
                                    jsonOrigin: JSON.stringify(supplier),
                                    jsonReturn: JSON.stringify(response)
                                });

                                setLoadingSupplierForm(false);
                                notifySuccess();
                                resetFields();
                                onClose();
                                setModalBox(false);
                            }
                        })
                } catch (err) {
                    createLog({
                        routine: "supplier",
                        action: "update",
                        type: "error",
                        message: `Fornecedor ${supplier.name} não alterado`,
                        jsonOrigin: JSON.stringify(supplier),
                        jsonReturn: JSON.stringify(err)
                    });

                    notifyError();
                }
            }
        }

        setLoadingSupplierForm(false);
    }

    function notifySuccess() {
        toast.success(`✔️ Fornecedor ${(isUpdate ? "alterado" : "incluído")} com sucesso!`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    function notifyError() {
        toast.error("❌ Ocorreu um problema ao salvar o fornecedor", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    function resetFields() {
        setSupplierId(undefined);
        setName("");
        setDocumentId("");
        setType(undefined);
        setPaymentType("");
        setCreateDate("");
        setAddressInfo({});
        setContactName("");
        setContactEmail("");
        setContactHomePhone("");
        setContactComercialPhone("");
        setContactCellPhone("");
        setObservation("");
        setActive(true);

        setInvalidName(false);
        setInvalidDocumentId(false);
        setInvalidDocumentIdText("Informe um CNPJ válido");
        setInvalidType(false);
    }

    async function maskDocumentField() {
        setInvalidDocumentId(false);

        const documentUnformatted = documentId.replace(/[.]/g, "").replace("/", "").replace("-", "");

        if (documentUnformatted.length > 0) {
            if (!cnpj.isValid(documentUnformatted)) {
                setInvalidDocumentId(true);
            } else {
                if (!isUpdate) {
                    await api.get(`/suppliers/document/${documentUnformatted}`)
                        .then(response => {
                            if (response.status === 200) {
                                setInvalidDocumentId(true);
                                setInvalidDocumentIdText("CNPJ já cadastrado");
                            }
                        });
                }
            }
        } else {
            setInvalidDocumentId(true);
        }
    }

    return (
        <>
            <Modal size="xl" fullscreen onHide={setModalBox} show={modalBox}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3 className="modal-title" id="exampleModalLabel">{(!isUpdate ? "Novo " : "Alterar ")}Fornecedor</h3>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setModalBox(false)}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="basic-form">
                            <div className="row" hidden={!isUpdate}>
                                <div className="form-group mb-3 col-md-6">
                                    <FormCheck
                                        type="switch"
                                        id="active"
                                        label="Ativo"
                                        defaultChecked={active}
                                        onChange={() => setActive(!active)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group mb-3 col-md-2">
                                    <label>CNPJ</label>
                                    <InputMask
                                        autoFocus
                                        mask="99.999.999/9999-99"
                                        maskChar=" "
                                        value={documentId}
                                        onChange={(event) => {
                                            setDocumentId(event?.target.value);
                                        }}
                                        onBlur={() => { maskDocumentField() }}
                                        alwaysShowMask={true}
                                        disabled={isUpdate}
                                    >
                                        {(inputProps) => <input {...inputProps} autoFocus type="text" className={`form-control ${(invalidDocumentId ? "is-invalid" : "")}`} disabled={isUpdate} />}
                                    </InputMask>
                                    <DFormText hidden={!invalidDocumentId} color="danger">{invalidDocumentIdText}</DFormText>
                                </div>
                                <div className="form-group mb-3 col-md-5">
                                    <label>Razão Social</label>
                                    <input
                                        autoFocus={isUpdate}
                                        type="text"
                                        className={`form-control ${(invalidName ? "is-invalid" : "")}`}
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                    />
                                    <DFormText hidden={!invalidName} color="danger">Informe o nome do fornecedor</DFormText>
                                </div>
                                <div className="form-group mb-3 col-md-3">
                                    <label>Nome Reduzido</label>
                                    <input
                                        type="text"
                                        maxLength={10}
                                        className="form-control"
                                        value={shortName}
                                        onChange={e => setShortName(e.target.value)}
                                    />
                                </div>
                                <div className="form-group mb-3 col-md-2">
                                    <label>Data de Cadastro</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={createDate}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group mb-3 col-md-3">
                                    <label>Tipo de Fornecedor</label>
                                    <Select
                                        className={`${(invalidType ? "is-invalid" : "")}`}
                                        defaultValue={type}
                                        value={type}
                                        onChange={(e) => setType(e)}
                                        options={TYPES_LIST}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                height: 45,
                                                backgroundColor: state.isDisabled ? "#dde0e3" : ""
                                            }),
                                        }}
                                        placeholder="Selecione..."
                                    />
                                    <DFormText hidden={!invalidType} color="danger">Campo obrigatório</DFormText>
                                </div>
                                <div className="form-group mb-3 col-md-2">
                                    <label>Forma de Pagamento</label>
                                    <input
                                        type="text"
                                        className={`form-control ${(invalidPaymentType ? "is-invalid" : "")}`}
                                        value={paymentType}
                                        onChange={(e) => setPaymentType(e.target.value)}
                                    />
                                    <DFormText hidden={!invalidPaymentType} color="danger">Informe a forma de pagamento</DFormText>
                                </div>
                            </div>

                            <DAddressForm
                                addressInfoParm={addressInfo}
                                onChangeAddress={setAddressInfo}
                            />

                            <h5>Contatos</h5>
                            <div className="row">
                                <div className="form-group mb-3 col-md-8">
                                    <label>Nome do Contato</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={contactName}
                                        onChange={e => setContactName(e.target.value)}
                                    />
                                </div>
                                <div className="form-group mb-3 col-md-4">
                                    <label>Email</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={contactEmail}
                                        onChange={e => setContactEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group mb-3 col-md-4">
                                    <label>Residencial</label>
                                    <InputMask
                                        mask="(99) 9999-9999"
                                        maskChar=" "
                                        value={contactHomePhone}
                                        onChange={(event) => {
                                            setContactHomePhone(event?.target.value);
                                        }}
                                        alwaysShowMask={true}
                                    >
                                        {(inputProps) => <input {...inputProps} type="text" className="form-control" />}
                                    </InputMask>
                                </div>
                                <div className="form-group mb-3 col-md-4">
                                    <label>Comercial</label>
                                    <InputMask
                                        mask="(99) 9999-9999"
                                        maskChar=" "
                                        value={contactComercialPhone}
                                        onChange={(event) => {
                                            setContactComercialPhone(event?.target.value);
                                        }}
                                        alwaysShowMask={true}
                                    >
                                        {(inputProps) => <input {...inputProps} type="text" className="form-control" />}
                                    </InputMask>
                                </div>
                                <div className="form-group mb-3 col-md-4">
                                    <label>Celular</label>
                                    <InputMask
                                        mask="(99) 99999-9999"
                                        maskChar=" "
                                        value={contactCellPhone}
                                        onChange={(event) => {
                                            setContactCellPhone(event?.target.value);
                                        }}
                                        alwaysShowMask={true}
                                    >
                                        {(inputProps) => <input {...inputProps} type="text" className="form-control" />}
                                    </InputMask>
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group mb-3 col-md-12">
                                    <label>Observações</label>
                                    <textarea
                                        type="text"
                                        className="form-control"
                                        value={observation}
                                        onChange={e => setObservation(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <Button
                            variant="outline-danger btn-rounded"
                            onClick={() => setModalBox(false)}
                        >
                            Fechar
                        </Button>
                        <Button
                            variant="success btn-rounded"
                            onClick={handleSaveSupplier}
                        >
                            Salvar
                        </Button>
                    </div>
                </div>
            </Modal>

            <ToastContainer />

            <ModalLoading
                visible={loadingSupplierForm}
                message="Gravando Informações..."
                onClise={setLoadingSupplierForm}
            />
        </>
    );
});

export default SupplierForm;
